import { ANALYTICS_FEATURES, type AnalyticsEvent, type AnalyticsFeature, type AnalyticsScreen } from "../shared/analytics";

export type AnalyticsContext = {
  screen: AnalyticsScreen;
  contentType?: AnalyticsEvent["contentType"];
  contentId?: string;
  reading?: boolean;
};

const FEATURES = new Set<string>(ANALYTICS_FEATURES);
const SESSION_IDLE_MS = 30 * 60_000;
const READING_IDLE_MS = 60_000;

export function activeReadingSeconds(elapsedMs: number, idleMs: number, visible: boolean): number {
  const elapsed = Math.max(0, Math.min(elapsedMs, 5_000));
  return visible ? Math.max(0, Math.min(elapsed, READING_IDLE_MS - idleMs + elapsed)) / 1_000 : 0;
}

export class AnalyticsClient {
  private context: AnalyticsContext = { screen: "home" };
  private queue: AnalyticsEvent[] = [];
  private sessionId = "";
  private sessionTouched = 0;
  private lastInput = Date.now();
  private lastTick = Date.now();
  private seconds = 0;
  private sending = 0;
  private foreground = document.visibilityState === "visible" && document.hasFocus();
  private stopped = false;
  private pageKey = "";
  private timer: ReturnType<typeof setInterval>;
  private storageKey: string;

  constructor(userId: string) {
    this.storageKey = `folio-analytics-session:${userId}`;
    try {
      const saved: unknown = JSON.parse(sessionStorage.getItem(this.storageKey) ?? "null");
      if (saved && typeof saved === "object" && "id" in saved && "at" in saved &&
          typeof saved.id === "string" && /^[0-9a-f-]{36}$/i.test(saved.id) && typeof saved.at === "number") {
        this.sessionId = saved.id;
        this.sessionTouched = saved.at;
      }
    } catch { /* Storage may be disabled; a memory-only session still works. */ }
    for (const name of ["pointerdown", "keydown", "scroll", "touchstart"]) document.addEventListener(name, this.activity, { capture: true, passive: true });
    document.addEventListener("click", this.click, true);
    document.addEventListener("visibilitychange", this.visibility);
    window.addEventListener("pagehide", this.pagehide);
    window.addEventListener("pageshow", this.focus);
    window.addEventListener("blur", this.blur);
    window.addEventListener("focus", this.focus);
    this.timer = setInterval(() => { this.tick(); if (this.seconds >= 15) this.flushReading(); void this.flush(); }, 5_000);
  }

  private activity = (): void => { this.tick(); this.lastInput = Date.now(); };
  private click = (event: Event): void => {
    const target = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-analytics-feature]") : null;
    const feature = target?.dataset.analyticsFeature;
    if (feature && FEATURES.has(feature)) this.feature(feature as AnalyticsFeature);
  };
  private visibility = (): void => {
    this.tick();
    this.foreground = document.visibilityState === "visible" && document.hasFocus();
    if (!this.foreground) { this.flushReading(); this.flushPending(); }
  };
  private blur = (): void => { this.tick(); this.foreground = false; };
  private focus = (): void => { this.lastTick = Date.now(); this.foreground = document.visibilityState === "visible" && document.hasFocus(); };
  private pagehide = (): void => { this.tick(); this.foreground = false; this.flushReading(); this.flushPending(); };

  private flushPending(): void {
    // Each keepalive request must be dispatched before the page is frozen,
    // including events recorded while an earlier request is still in flight.
    while (this.queue.length > 0) void this.flush(true);
  }

  private tick(): void {
    const now = Date.now();
    if (this.context.reading && this.context.contentId) {
      this.seconds += activeReadingSeconds(now - this.lastTick, now - this.lastInput,
        this.foreground && !document.body.classList.contains("reader-toc-open"));
    }
    this.lastTick = now;
  }

  private event(type: AnalyticsEvent["type"], extra: Partial<Pick<AnalyticsEvent, "feature" | "durationSeconds">> = {}): void {
    if (this.stopped) return;
    const now = Date.now();
    if (!this.sessionId || now - this.sessionTouched >= SESSION_IDLE_MS) {
      this.sessionId = crypto.randomUUID();
      this.enqueue({ id: crypto.randomUUID(), sessionId: this.sessionId, type: "session_start", screen: this.context.screen });
    }
    this.sessionTouched = now;
    try { sessionStorage.setItem(this.storageKey, JSON.stringify({ id: this.sessionId, at: now })); } catch { /* Optional browser storage. */ }
    this.enqueue({ id: crypto.randomUUID(), sessionId: this.sessionId, type, screen: this.context.screen,
      ...(this.context.contentId ? { contentType: this.context.contentType, contentId: this.context.contentId } : {}), ...extra });
  }

  private enqueue(event: AnalyticsEvent): void {
    // Keep retries bounded during a prolonged outage, without blocking reading.
    if (this.queue.length < 180) this.queue.push(event);
  }

  setContext(next: AnalyticsContext): void {
    this.tick();
    this.flushReading();
    this.context = next;
    const key = `${next.screen}:${next.contentType ?? ""}:${next.contentId ?? ""}`;
    if (key !== this.pageKey) {
      this.pageKey = key;
      this.event("page_view");
    }
  }

  feature(feature: AnalyticsFeature): void { this.event("feature_click", { feature }); }

  pauseReading(): void {
    this.tick();
    this.flushReading();
    this.context = { ...this.context, reading: false };
  }

  private flushReading(): void {
    const durationSeconds = Math.floor(this.seconds);
    this.seconds = 0;
    if (durationSeconds > 0 && this.context.contentId) this.event("read_time", { durationSeconds: Math.min(durationSeconds, 60) });
  }

  async flush(leaving = false): Promise<void> {
    if ((!leaving && this.sending > 0) || this.queue.length === 0) return;
    this.sending += 1;
    const batch = this.queue.splice(0, 30);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10_000);
    try {
      const response = await fetch("/api/analytics/events", {
        method: "POST", credentials: "same-origin", keepalive: true, signal: controller.signal,
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ events: batch }),
      });
      if (!response.ok && (response.status >= 500 || response.status === 429)) this.queue = [...batch, ...this.queue].slice(0, 180);
    } catch { this.queue = [...batch, ...this.queue].slice(0, 180); }
    finally { clearTimeout(timeout); this.sending -= 1; }
  }

  stop(): void {
    if (this.stopped) return;
    this.tick(); this.flushReading(); this.flushPending(); this.stopped = true;
    clearInterval(this.timer);
    for (const name of ["pointerdown", "keydown", "scroll", "touchstart"]) document.removeEventListener(name, this.activity, true);
    document.removeEventListener("click", this.click, true);
    document.removeEventListener("visibilitychange", this.visibility);
    window.removeEventListener("pagehide", this.pagehide);
    window.removeEventListener("pageshow", this.focus);
    window.removeEventListener("blur", this.blur);
    window.removeEventListener("focus", this.focus);
  }
}

let client: AnalyticsClient | null = null;
export function startAnalytics(userId: string): () => void {
  client?.stop();
  const instance = new AnalyticsClient(userId);
  client = instance;
  return () => { instance.stop(); if (client === instance) client = null; };
}
export function setAnalyticsContext(context: AnalyticsContext): void { client?.setContext(context); }
export function trackFeature(feature: AnalyticsFeature): void { client?.feature(feature); }
export function pauseAnalyticsReading(): void { client?.pauseReading(); }
